import React, { useEffect, useRef, useState } from "react";
import { Check, MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  VisitOutcome,
  AttendanceSide,
  outcomeSide,
  outcomeQualifier,
  DEFAULT_PRESENT_OUTCOME,
  DEFAULT_ABSENT_OUTCOME,
  REFINABLE_OUTCOMES,
} from "../../features/sessions/models/VisitOutcome";

interface AttendanceOutcomeControlProps {
  outcome: VisitOutcome;
  onChange: (outcome: VisitOutcome) => void;
  /** The Visit's own Payment Type name (e.g. a late-cancellation fee), shown next to "Charged". */
  feeName?: string;
}

/**
 * Per-member control: a one-tap Present / Absent toggle for the common case, plus
 * a "···" menu that refines to the billing-aware outcomes (comp, charged, waived).
 */
const AttendanceOutcomeControl: React.FC<AttendanceOutcomeControlProps> = ({
  outcome,
  onChange,
  feeName,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const side: AttendanceSide = outcomeSide(outcome);
  const qualifier = outcomeQualifier(outcome);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const sideButton = (
    target: AttendanceSide,
    defaultOutcome: VisitOutcome,
    label: string,
    activeClasses: string
  ) => {
    const isActive = side === target;
    return (
      <button
        type="button"
        onClick={() => {
          if (side !== target) onChange(defaultOutcome);
        }}
        aria-pressed={isActive}
        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
          isActive
            ? activeClasses
            : "bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-70 hover:bg-grey-90 dark:hover:bg-primary-20"
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div ref={containerRef} className="relative flex items-center gap-2">
      {qualifier && (
        <span className="text-xs font-medium text-grey-30 dark:text-grey-70">
          {qualifier === "charged" && feeName
            ? t("sessions.attendance.shortLabelWithFee", {
                charged: t("sessions.attendance.shortLabel.charged"),
                name: feeName,
              })
            : t(`sessions.attendance.shortLabel.${qualifier}`)}
        </span>
      )}

      <div className="flex overflow-hidden rounded-lg border border-grey-90 dark:border-primary-20">
        {sideButton(
          "present",
          DEFAULT_PRESENT_OUTCOME,
          t("sessions.attendance.present"),
          "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
        )}
        {sideButton(
          "absent",
          DEFAULT_ABSENT_OUTCOME,
          t("sessions.attendance.absent"),
          "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
        )}
      </div>

      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label={t("sessions.attendance.refine")}
        className="flex size-8 items-center justify-center rounded-lg text-grey-30 dark:text-grey-70 hover:bg-grey-95 dark:hover:bg-primary-20"
      >
        <MoreHorizontal size={18} />
      </button>

      {menuOpen && (
        <div className="absolute right-0 top-9 z-20 w-56 rounded-xl border border-grey-90 bg-white p-1 shadow-lg dark:border-primary-20 dark:bg-primary-10">
          {REFINABLE_OUTCOMES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                onChange(option);
                setMenuOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-grey-10 hover:bg-grey-95 dark:text-grey-90 dark:hover:bg-primary-20"
            >
              <span>{t(`sessions.attendance.outcomes.${option}`)}</span>
              {outcome === option && (
                <Check size={16} className="text-green-700 dark:text-green-400" />
              )}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              onChange(VisitOutcome.Unmarked);
              setMenuOpen(false);
            }}
            className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
          >
            <span>{t("sessions.attendance.clear")}</span>
            {outcome === VisitOutcome.Unmarked && (
              <Check size={16} className="text-grey-30 dark:text-grey-70" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default AttendanceOutcomeControl;
